import { useMemo, useEffect, useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useHomePagesQuery, HomePage } from 'graphql-common';
import { TabBarItem } from '@lib/components/TabBar/constants';
import useTabParam from '@lib/hooks/useTabParam';

/** Return type for useHomePages hook */
interface UseHomePagesResult {
  // Home pages data
  /** Sorted list of home pages */
  homePages: HomePage[];
  /** Tab bar items for home pages */
  tableTabs: TabBarItem[];

  // Active tab state
  /** Currently active home page ID */
  activeHomePageId: string | undefined;
  /** Whether the active home page is the default (General) tab */
  isActiveHomePageDefault: boolean;
  /** Handler to set active home page ID */
  setActiveHomePageId: (id: string) => void;
  /** Handler for tab change from TabBar */
  onTabChange: (item: TabBarItem) => void;

  // Loading and error states
  /** Loading state for home pages query */
  homePagesLoading: boolean;
  /** Error from home pages query */
  homePagesError: ApolloError | undefined;

  // Refetch
  /** Function to refetch home pages */
  refetchHomePages: () => void;
}

/**
 * Hook for managing home pages data and tab state
 * Handles fetching home pages, sorting, tab generation, and active tab management
 */
export default function useHomePages(isEditMode: boolean): UseHomePagesResult {
  // URL-synced tab state
  const [activeHomePageId, setActiveHomePageId] = useTabParam({
    usePagination: false,
  });

  // Fetch home pages
  const {
    data: homePagesData,
    loading: homePagesLoading,
    error: homePagesError,
    refetch: refetchHomePages,
  } = useHomePagesQuery({
    variables: {
      page: 1,
      limit: 1000,
    },
  });

  // Sort home pages by position
  const homePagesCollection = homePagesData?.data?.collection;
  const homePages = useMemo(
    () =>
      Array.isArray(homePagesCollection)
        ? [...homePagesCollection].sort((a, b) => a.position - b.position)
        : [],
    [homePagesCollection],
  );

  // Auto-select first tab if none selected
  useEffect(() => {
    if (activeHomePageId || !homePages.length) {
      return;
    }

    const firstPageId = homePages[0]?.id;
    if (firstPageId) {
      setActiveHomePageId(firstPageId);
    }
  }, [activeHomePageId, homePages, setActiveHomePageId]);

  // Generate tab bar items from home pages
  const tableTabs = useMemo<TabBarItem[]>(() => {
    return homePages.map((homePage) => ({
      label: homePage.name,
      value: homePage.id,
      disabled: isEditMode,
    }));
  }, [homePages, isEditMode]);

  // Check if active home page is the default (General) tab
  const isActiveHomePageDefault = useMemo(() => {
    const activeHomePage = homePages.find((hp) => hp.id === activeHomePageId);
    return activeHomePage?.isDefault ?? true;
  }, [homePages, activeHomePageId]);

  // Tab change handler for TabBar component
  const onTabChange = useCallback(
    (item: TabBarItem) => {
      setActiveHomePageId(item.value);
    },
    [setActiveHomePageId],
  );

  return {
    // Home pages data
    homePages,
    tableTabs,

    // Active tab state
    activeHomePageId,
    isActiveHomePageDefault,
    setActiveHomePageId,
    onTabChange,

    // Loading and error states
    homePagesLoading,
    homePagesError,

    // Refetch
    refetchHomePages,
  };
}
